import React, { useState, useEffect, ReactNode, useCallback } from 'react';
import {
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    IconButton,
    Tabs,
    Tab,
    Typography, Paper, MenuItem,
    Button,
    Alert,
    Modal, Autocomplete,
    Chip,
    Tooltip,
    Divider
} from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { LoadingButton } from '@mui/lab';
import { Localized, useLocalization } from '@fluent/react';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import AddLinkIcon from '@mui/icons-material/AddLink';

import {
    createDocumentTest,
    updateDocumentTest,
    DocumentTest,
    DocumentTestAssignment,
    DocumentTestRoleType,
    CreateDocumentTest,
    DocumentTestParentObjectType,
} from '../documentTestsApi';

import { TestPhase, TestPhaseType, TestSubType } from '../../../testSpecification/testSpecificationApi';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { showError, showSuccess } from '../../../notifications/notificationsSlice';
import DocumentTestPhaseTab from './DocumentTestPhaseTab';
import { SectionComments } from '../../SectionComments';
import { docTestsSelectors, isDocEditable, selectDocument, showDocComments } from '../../documentSlice';
import { RichTextContainer, tiptapExtensions } from '../../../../app/RichTextEditor';
import { impossible } from '../../../../app/Utils';
import { loadDocumentProcess } from '../../../project/projectProcess/ProjectProcessApiClient';
import { UrsRequirementBrowser } from '../../../urs/UrsRequirementBrowser';
import { allURSRequirements, defURSRequirement, loadUrsOnly, Urs, URSRequirement } from '../../../urs/UrsSlice';
import EditIcon from '@mui/icons-material/Edit';
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import AutoFixNormalIcon from "@mui/icons-material/AutoFixNormal";
import { ValidationDescriptionPrompt, ValidationMethodologyPrompt } from '../utils/DocumentTestsPrompts';
import { postWithAuth } from '../../../../http';
import { useEditor } from '@tiptap/react';
import { unwrapResult } from '@reduxjs/toolkit';
import { ProcessDocumentOverview } from '../../../project/validationProcess/validationProcessApi';
import { LoadingContainer } from '../../../../app/LoadingContainer';
import { allRAFunctions, loadRiskAnalysisContent, RiskAnalysisContent, RiskAnalysisDefect, RiskAnalysisFunction } from '../../riskAnalysis/riskAnalysisApi';
import { addCacheEntry, tryGetCacheEntry } from '../../cacheSlice';
import produce from "immer";
import { RiskAnalysisFunctionBrowser } from '../../../riskAnalysis/RiskAnalysisFunctionBrowser';
import { AiGenerationQuestionDialog } from './AiGenerationQuestionDialog';

export type RelatedObject
    = { tag: 'URS', data: URSRequirement, docId: string, docSymbol: string, loaded: boolean }
    | { tag: 'RA', data: RiskAnalysisFunction, docId: string, docSymbol: string, loaded: boolean }

interface EditDocumentTestDialogProps {
    testId?: number
    documentId: number;
    sectionId: string;
    mode: 'create' | 'edit';
    onSave: (test: DocumentTest) => void;
    onCancel: () => void;
    relatedObjId?: number
    relatedObjType?: string
    relatedObj?: RelatedObject
    stage?: TestPhaseType
}

export const EditDocumentTestDialog = (props: EditDocumentTestDialogProps) => {
    const {
        testId,
        documentId,
        sectionId,
        onSave,
        onCancel,
        mode,
        stage,
    } = props
    const dispatch = useAppDispatch();
    const { l10n } = useLocalization();

    const { header, activeProcess } = useAppSelector(selectDocument)?.doc ?? impossible()

    const testSpecification = useAppSelector(state => testId ? docTestsSelectors.selectById(state, testId) : undefined)

    const ursDocs: ProcessDocumentOverview[] = activeProcess?.stages.find(s => s.stage === 'URS')?.documents ?? []
    const raDocs: ProcessDocumentOverview[] = activeProcess?.stages.find(s => s.stage === 'RA')?.documents ?? []
    const projectDocs = ursDocs.concat(raDocs)
    const [currentDoc, setCurrentDoc] = useState<ProcessDocumentOverview | null>(() => {
        if (testSpecification && testSpecification.parentObjectId) {
            const doc = projectDocs.find(d => d.id === testSpecification.parentObjectId)
            return doc ?? null
        } else {
            return projectDocs.length > 0 ? projectDocs[0] : null
        }
    }
    )
    const handleStageChange = (_, newStage) => {
        setCurrentDoc(newStage ?? null)
    }

    const [fullScreen, setFullScreen] = useState(true);

    const [mainTab, setMainTab] = useState(0);

    const spec = testSpecification?.testSpecification;

    const [testName, setTestName] = useState(spec?.name ?? '');
    const [testDescription, setTestDescription] = useState(spec?.description ?? '');
    const [testMetodology, setTestMetodology] = useState(spec?.metodology ?? '');
    const [assignModal, setAssignModal] = useState(false)
    const [testPhaseType, setTestPhaseType] = useState<TestPhaseType>(() => {
        if (stage) {
            return stage
        } else {
            return testSpecification?.testSpecification.testPhases[0].phaseType ?? 'PQ'
        }
    })
    const [saving, setSaving] = useState(false);
    const [generatingDescription, setGeneratingDescription] = useState(false);
    const [generatingMethodology, setGeneratingMethodology] = useState(false);
    const [generationQuestion, setGenerationQuestion] = useState<{ type: 'test-description' | 'test-methodology', question: string } | null>(null);
    const [generationError, setGenerationError] = useState<string | null>(null);

    const [relatedObject, setRelatedObject] = useState<RelatedObject | null>(() => {
        if (props.relatedObj) {
            return props.relatedObj
        } else if (testSpecification && testSpecification.relatedObjectId && testSpecification.relatedObjectType) {
            switch (testSpecification.relatedObjectType) {
                case 'REQUIREMENT':
                    return {
                        tag: 'URS',
                        data: {
                            ...defURSRequirement,
                            id: testSpecification.relatedObjectId.toString(),
                        },
                        docId: testSpecification.parentObjectId?.toString() ?? '',
                        docSymbol: testSpecification.parentObjectType ?? '',
                        loaded: false,
                    }
                default:
                    return null
            }
        } else {
            return null
        }
    })

    useEffect(() => {
        const f = async () => {
            if (relatedObject && relatedObject.tag === 'URS' && relatedObject.loaded === false) {
                let urs: Urs | null = null
                const { docId, data: req } = relatedObject
                const cached = tryGetCacheEntry(docId, 'urs')
                if (cached.tag === 'ok') {
                    urs = cached.data as Urs
                } else {
                    urs = await dispatch(loadUrsOnly(docId)).unwrap()
                    dispatch(addCacheEntry({
                        id: docId,
                        data: urs,
                        type: 'urs',
                    }))
                }
                if (urs != null) {
                    const allRequirements = Array.from(allURSRequirements(urs))
                    const requirement = allRequirements.find(r => r.id === req.id)
                    if (requirement) {
                        setRelatedObject(produce(draft => {
                            if (draft && draft.tag === 'URS') {
                                draft.data.name = requirement.name
                                draft.data.numeration = requirement.numeration
                                draft.docSymbol = urs?.ursNo ?? ''
                                draft.loaded = true
                            }
                        }))
                    }
                }
            } else if (relatedObject && relatedObject.tag === 'RA' && relatedObject.loaded === false) {
                console.log('Loading RA...')
                let ra: RiskAnalysisContent | null = null
                const { docId, data: fn } = relatedObject
                const cached = tryGetCacheEntry(docId, 'ra')
                if (cached.tag === 'ok') {
                    ra = cached.data as RiskAnalysisContent
                } else {
                    ra = await dispatch(loadRiskAnalysisContent(docId)).unwrap()
                    dispatch(addCacheEntry({
                        id: docId,
                        data: ra,
                        type: 'ra',
                    }))
                }
                if (ra !== null) {
                    const allFns = Array.from(allRAFunctions(ra))
                    const someFunction = allFns.find(f => f.id === fn.id)
                    if (someFunction) {
                        setRelatedObject(produce(draft => {
                            if (draft !== null && draft.tag === 'RA') {
                                draft.data.name = someFunction.name
                                draft.data.numeration = someFunction.numeration
                                draft.data.defects = someFunction.defects
                                draft.loaded = true
                            }
                        }))
                    }
                }
            }
        }
        f()
    }, [])

    const [loading, setLoading] = useState(false)

    const isEdit = !!testSpecification;
    const showComments = useAppSelector(state => showDocComments(state, sectionId))
    const editable = useAppSelector(isDocEditable) && header.documentKind !== 'REPORT'

    const createEmptyPhase = (): TestPhase => ({
        id: 0,
        phaseType: 'GENERAL' as any,
        isActive: true,
        subType: 'NONE',
        testSpecificationId: '0',
        steps: [],
    });

    const [testPhases, setTestPhases] = useState<TestPhase[]>(
        isEdit
            ? (spec?.testPhases || [])
            : (spec?.testPhases?.length ? [spec.testPhases[0]] : [createEmptyPhase()])
    );

    const currentPhase =
        testPhases.find(p => p.phaseType === testPhaseType) ?? testPhases[0];

    const [plannedStart, setPlannedStart] = useState<string>(testSpecification?.plannedStart ?? '');
    const [plannedEnd, setPlannedEnd] = useState<string>(testSpecification?.plannedEnd ?? '');

    const [testerName, setTesterName] = useState('');
    const [testerEmail, setTesterEmail] = useState('');
    const [approverName, setApproverName] = useState('');
    const [approverEmail, setApproverEmail] = useState('');


    useEffect(() => {
        const a = testSpecification?.assignments || [];
        const pick = (role: DocumentTestRoleType) => a.find(x => x.role === role);
        const ex = pick('TESTER');
        const ap = pick('APPROVER');
        if (ex) {
            setTesterName(ex.name ?? '');
            setTesterEmail(ex.email ?? '');
        }
        if (ap) {
            setApproverName(ap.name ?? '');
            setApproverEmail(ap.email ?? '');
        }
    }, [testSpecification]);

    useEffect(() => {
        if (!activeProcess) {
            setLoading(true)
            dispatch(loadDocumentProcess(documentId.toString()))
                .then(unwrapResult)
                .then(proc => {
                    if (testSpecification) {
                        const { parentObjectId, parentObjectType } = testSpecification
                        if (parentObjectId && parentObjectType) {
                            let objType: DocumentTestParentObjectType = 'URS'
                            switch (parentObjectType) {
                                case 'URS':
                                    objType = 'URS'
                                    break;
                                case 'RA':
                                    objType = 'RA'
                                    break;
                            }
                            const stage = proc.stages.find(s => s.stage === objType)
                            if (stage) {
                                const doc = stage.documents.find(d => d.id === parentObjectId)
                                if (doc) {
                                    setCurrentDoc(doc)
                                }
                            }
                        }
                    }
                })
                .catch(err => console.log('Error loading process info: ', err))
                .finally(() => setLoading(false))
        }
    }, [])

    const [phaseTab, setPhaseTab] = useState(0);
    const hasAnyPhases = (testPhases?.length ?? 0) > 0;
    const hasPhaseTabs = (testPhases?.length ?? 0) > 1;

    const toggleFullScreen = () => setFullScreen((prev) => !prev);
    const handleMainTabChange = (_: React.SyntheticEvent, v: number) => setMainTab(v);
    const handlePhaseTabChange = (_: React.SyntheticEvent, v: number) => setPhaseTab(v);

    const handleAssignClick = useCallback(() => setAssignModal(true), [])
    const handlecancelAssignClick = useCallback(() => setAssignModal(false), [])

    const buildAssignments = (): DocumentTestAssignment[] => {
        const list: DocumentTestAssignment[] = [];
        if (testerName || testerEmail) {
            list.push({
                role: 'TESTER',
                name: testerName || undefined,
                email: testerEmail || undefined,
            });
        }
        if (approverName || approverEmail) {
            list.push({
                role: 'APPROVER',
                name: approverName || undefined,
                email: approverEmail || undefined,
            });
        }
        return list;
    };

    const handleSave = async () => {

        if (!testName?.trim()) {
            dispatch(showError('Nazwa testu jest wymagana.'));
            return;
        }

        if (plannedStart && plannedEnd && plannedEnd < plannedStart) {
            dispatch(showError('Plan koniec nie może być wcześniejszy niż plan start.'));
            return;
        }

        setSaving(true);
        try {
            const assignments = buildAssignments();

            if (mode === 'edit') {
                if (!testSpecification) {
                    dispatch(showError('Brak danych do edycji.'));
                    setSaving(false);
                    return;
                }

                const updated: any = {
                    ...testSpecification,
                    plannedStart: plannedStart || null,
                    plannedEnd: plannedEnd || null,
                    assignments,
                    testSpecification: {
                        ...spec,
                        name: testName.trim(),
                        description: testDescription ?? '',
                        metodology: testMetodology,
                        testPhases: testPhases ?? [],
                    },
                };

                const result = await dispatch(updateDocumentTest(updated));
                if ((updateDocumentTest as any).fulfilled.match(result)) {
                    dispatch(showSuccess('Zapisano zmiany.'));
                    onSave(result.payload as DocumentTest);
                } else {
                    const errorMsg =
                        ((result as any).payload?.problem?.title as string) ||
                        (result as any).payload ||
                        'error';
                    dispatch(showError(errorMsg));
                }
            } else {
                let relatedObjType: string | undefined = undefined
                if (props.relatedObjType) {
                    relatedObjType = props.relatedObjType
                } else if (relatedObject) {
                    switch (relatedObject.tag) {
                        case 'URS':
                            relatedObjType = 'REQUIREMENT'
                            break;
                        case 'RA':
                            relatedObjType = 'FUNCTION'
                            break;
                    }
                }
                let relatedObjId: number | undefined = undefined
                if (props.relatedObjId) {
                    relatedObjId = props.relatedObjId
                } else if (relatedObject) {
                    switch (relatedObject.tag) {
                        case 'URS':
                            relatedObjId = parseInt(relatedObject.data.id)
                            break;
                        case 'RA':
                            relatedObjId = 0
                            break;
                    }
                }
                const payload: CreateDocumentTest = {
                    documentId,
                    sectionId,
                    plannedStart: plannedStart || null,
                    plannedEnd: plannedEnd || null,
                    assignments,
                    relatedObjectType: relatedObjType ?? null,
                    relatedObjectId: relatedObjId ?? null,
                    parentObjectId: relatedObject?.docId ?? null,
                    parentObjectType: relatedObject?.tag as DocumentTestParentObjectType ?? null,
                    testSpecification: {
                        name: testName.trim(),
                        description: testDescription ?? '',
                        metodology: testMetodology,
                        testPhases: testPhases.map(p => {
                            return {
                                ...p,
                                phaseType: testPhaseType,
                            }
                        }) ?? [],
                    },
                };

                const result = await dispatch(createDocumentTest(payload));
                if ((createDocumentTest as any).fulfilled.match(result)) {
                    dispatch(showSuccess('Utworzono test.'));
                    onSave(result.payload as DocumentTest);
                } else {
                    const errorMsg =
                        ((result as any).payload?.problem?.title as string) ||
                        (result as any).payload ||
                        'error';
                    dispatch(showError(errorMsg));
                }
            }
        } catch {
            dispatch(showError('error'));
        } finally {
            setSaving(false);
        }
    };
    const prepareDescriptionQuestion = () => {
        if (!testName?.trim()) {
            dispatch(showError("Nazwa testu jest wymagana do generowania opisu."));
            return null;
        }
        let requirement: string | null = null
        if (relatedObject && relatedObject.loaded) {
            switch (relatedObject.tag) {
                case 'URS':
                    requirement = relatedObject.data.name
                    break;
                case 'RA':
                    requirement = relatedObject.data.name
                    break;
            }
        }


        return ValidationDescriptionPrompt.build({
            testName,
            phaseType: testPhaseType,
            requirement,
        });
    };

    const handleOpenDescriptionQuestion = () => {
        if (!editable) return;
        const question = prepareDescriptionQuestion();
        if (question === null) return;
        setGenerationQuestion({ type: 'test-description', question });
        setGenerationError(null);
    };

    const handleQuickDescription = () => {
        if (!editable || generatingDescription) return;
        const question = prepareDescriptionQuestion();
        if (question !== null) void handleGenerateDescription(question, true);
    };

    const handleGenerateDescription = async (question: string, useSystemPrompt = false) => {
        if (generatingDescription) return;
        const reportError = (error: string) => {
            if (useSystemPrompt) dispatch(showError(error));
            else setGenerationError(error);
        };
        setGeneratingDescription(true);
        setGenerationError(null);
        try {
            const result = await dispatch(
                postWithAuth({
                    url: `api/document-tests/${documentId}/ai/generate`,
                    payload: {
                        question,
                        type: "test-description",
                        testName,
                        testPhase: testPhaseType,
                        useSystemPrompt
                    },
                })
            );

            if (postWithAuth.fulfilled.match(result)) {
                const { payload } = result;

                if (typeof payload.answer !== "string" || !payload.answer.trim()) {
                    reportError("validation-ai-error");
                    return;
                }

                var answer = payload.answer.trim()
                setTestDescription(answer);
                descriptionEditor?.commands.setContent(answer)
                setGenerationQuestion(null);
            } else {
                const httpPayload = result.payload;
                if (httpPayload?.kind === "http") {
                    reportError(httpPayload.problem?.title ?? "error");
                } else {
                    reportError("error");
                }
            }
        } catch {
            reportError("error");
        } finally {
            setGeneratingDescription(false);
        }
    };

    const prepareMethodologyQuestion = () => {
        if (!testName?.trim()) {
            dispatch(showError("Nazwa testu jest wymagana do generowania metodologii."));
            return null;
        }

        if (!testDescription?.trim()) {
            dispatch(showError("Opis testu jest wymagany do generowania metodologii."));
            return null;
        }

        return ValidationMethodologyPrompt.build({
            testName,
            phaseType: currentPhase.phaseType,
            testDescription,
        });
    };

    const handleOpenMethodologyQuestion = () => {
        if (!editable) return;
        const question = prepareMethodologyQuestion();
        if (question === null) return;
        setGenerationQuestion({ type: 'test-methodology', question });
        setGenerationError(null);
    };

    const handleQuickMethodology = () => {
        if (!editable || generatingMethodology) return;
        const question = prepareMethodologyQuestion();
        if (question !== null) void handleGenerateMethodology(question, true);
    };

    const handleGenerateMethodology = async (question: string, useSystemPrompt = false) => {
        if (generatingMethodology) return;
        const reportError = (error: string) => {
            if (useSystemPrompt) dispatch(showError(error));
            else setGenerationError(error);
        };
        setGeneratingMethodology(true);
        setGenerationError(null);
        try {
            let relatedObjectDescription = ''
            if (relatedObject !== null) {
                switch (relatedObject.tag) {
                    case 'URS':
                        relatedObjectDescription = relatedObject.data.name
                        break;
                    case 'RA':
                        relatedObjectDescription = relatedObject.data.name
                        break;
                }
            }

            const result = await dispatch(
                postWithAuth({
                    url: `api/document-tests/${documentId}/ai/generate`,
                    payload: {
                        question,
                        type: "test-methodology",
                        testName,
                        relatedObjectDescription,
                        useSystemPrompt
                    },
                })
            );

            if (postWithAuth.fulfilled.match(result)) {
                const { payload } = result;

                if (typeof payload.answer !== "string" || !payload.answer.trim()) {
                    reportError("validation-ai-error");
                    return;
                }

                var answer = payload.answer.trim()
                setTestMetodology(answer);
                metodologyEditor?.commands.setContent(answer)
                setGenerationQuestion(null);
            } else {
                const httpPayload = result.payload;
                if (httpPayload?.kind === "http") {
                    reportError(httpPayload.problem?.title ?? "error");
                } else {
                    reportError("error");
                }
            }
        } catch {
            reportError("error");
        } finally {
            setGeneratingMethodology(false);
        }
    };

    const updatePhaseSteps = (phaseId: number, updatedSteps: TestPhase['steps']) => {
        setTestPhases((prev) => prev.map((phase) => (phase.id === phaseId ? { ...phase, steps: updatedSteps } : phase)));
    };
    const updatePhaseSubType = (phaseId: number, subType: TestSubType) => {
        setTestPhases((prev) => prev.map((phase) => (phase.id === phaseId ? { ...phase, subType } : phase)));
    };

    const descriptionEditor = useEditor({
        extensions: tiptapExtensions,
        content: testDescription,
        editable: editable,
        onUpdate: (e) => {
            setTestDescription(e.editor.getHTML())
        },
    })
    const metodologyEditor = useEditor({
        extensions: tiptapExtensions,
        content: testMetodology,
        editable: editable,
        onUpdate: (e) => {
            setTestMetodology(e.editor.getHTML())
        },
    })

    const setRelatedUrsRequirement = useCallback((req: URSRequirement | null) => {
        if (req !== null) {
            setRelatedObject({
                tag: 'URS',
                data: req,
                docId: currentDoc?.id?.toString() ?? '',
                docSymbol: `${currentDoc?.title ?? ''} (${currentDoc?.no ?? ''}) v.${currentDoc?.version}`,
                loaded: true,
            })
        } else {
            setRelatedObject(null)
        }
    }, [currentDoc])

    const setRelatedFunction = useCallback((fn: RiskAnalysisFunction | null) => {
        if (fn !== null) {
            setRelatedObject({
                tag: 'RA',
                data: fn,
                docId: currentDoc?.id?.toString() ?? '',
                docSymbol: `${currentDoc?.title ?? ''} (${currentDoc?.no ?? ''}) v.${currentDoc?.version}`,
                loaded: true,
            })
        } else {
            setRelatedObject(null)
        }
    }, [currentDoc])

    const title = mode === 'edit' ?
        <Localized id="test-edit-title"><span>_Edit test</span></Localized> :
        <Localized id="test-create-title"><span>_Create test</span></Localized>;

    const showAssignButton = relatedObject === null

    const actions: ReactNode = <IconButton sx={{
        ml: 'auto',
    }} size='small' onClick={() => setAssignModal(true)}>
        <EditIcon />
    </IconButton>

    let content: ReactNode = <></>

    if (loading) {
        content = <LoadingContainer />
    } else {

        content = <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 1 }}>
            <Box display="flex" flexDirection="column" gap={3} sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                <RelatedObjectPanel data={relatedObject} actions={actions} />
                {showAssignButton && <Box sx={{
                    display: 'flex',
                    gap: 2,
                }}>
                    <Alert severity='warning' variant='outlined'>Brak skojarzenia z wymaganiem z dokumentu URS/Analizy ryzyka</Alert>
                    <Button onClick={handleAssignClick} size='small' startIcon={<AddLinkIcon />} variant='outlined'>Skojarz</Button>
                </Box>}
                <TextField
                    label={<Localized id="test-name">_test-name</Localized>}
                    value={testName}
                    onChange={(e) => setTestName(e.target.value)}
                    sx={{ mt: '10px' }}
                    fullWidth
                    disabled={!editable}
                />
                <TextField
                    select
                    value={testPhaseType}
                    onChange={e => {
                        const v = e.target.value as TestPhaseType;
                        setTestPhaseType(v);
                        setTestPhases(prev => prev.map((p, idx) =>
                            idx === phaseTab ? { ...p, phaseType: v } : p
                        ));
                    }}
                    disabled={!editable || stage !== undefined}
                    label={<Localized id="validation-stage">Etap</Localized>} >
                    {["IQ", "OQ", "DQ", "PQ"].map(stage => <MenuItem key={stage} value={stage}>{stage}</MenuItem>)}
                </TextField>
                <Tabs value={mainTab} onChange={handleMainTabChange} variant="fullWidth" sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tab label={<Localized id="description">
                        <span>_description</span>
                    </Localized>} />
                    <Tab label={<Localized id="test-methodology">
                        <span>_metodologia testu</span>
                    </Localized>} />
                    <Tab label={<Localized id="test-procedure">
                        <span>_przebieg testu</span>
                    </Localized>} />
                </Tabs>
                {mainTab === 0 && (
                    <Box>
                        <Paper variant="outlined">
                            <Box
                                px={1.5}
                                py={1}
                                bgcolor={(t) => t.palette.grey[100]}
                                borderBottom={(t) => `1px solid ${t.palette.divider}`}
                            >
                                <Typography variant="subtitle2" fontWeight={700}>
                                    Opis testu
                                </Typography>
                            </Box>

                            <Box
                                sx={{
                                    p: 2,
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: 1,
                                }}
                            >
                                {/* Editor – zajmuje całą dostępną szerokość */}
                                <Box sx={{ flex: 1 }}>
                                    <RichTextContainer
                                        label={l10n.getString("test-description", null, "test-description")}
                                        editor={descriptionEditor}
                                    />
                                </Box>

                                <Tooltip title={l10n.getString("test-edit-ai-question", null, "_Edit AI question")}>
                                    <span>
                                        <IconButton
                                            color="primary"
                                            size="small"
                                            disabled={!editable || generatingDescription}
                                            onClick={handleOpenDescriptionQuestion}
                                            sx={{ mt: "28px" }}
                                        >
                                            <AutoFixHighIcon
                                                sx={{
                                                    "@keyframes spin": {
                                                        "0%": { transform: "rotate(0deg)" },
                                                        "100%": { transform: "rotate(360deg)" },
                                                    },
                                                    animation: generatingDescription ? "spin 0.8s linear infinite" : "none",
                                                }}
                                            />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <Tooltip title={l10n.getString("test-generate-system-prompt", null, "_Generate with system prompt")}>
                                    <span>
                                        <IconButton
                                            color="secondary"
                                            size="small"
                                            disabled={!editable || generatingDescription}
                                            onClick={handleQuickDescription}
                                            sx={{ mt: "28px" }}
                                        >
                                            <AutoFixNormalIcon />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Box>

                        </Paper>

                    </Box>
                )}
                {mainTab === 1 && (

                    <Box>
                        <Paper variant="outlined" sx={{ overflow: 'hidden', borderRadius: 2 }}>
                            <Box
                                px={1.5}
                                py={1}
                                bgcolor={(t) => t.palette.grey[100]}
                                borderBottom={(t) => `1px solid ${t.palette.divider}`}
                            >
                                <Typography variant="subtitle2" fontWeight={700}>
                                    Metodologia
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    p: 2,
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: 1,
                                }}
                            >

                                <Box sx={{ flex: 1 }}>
                                    <RichTextContainer
                                        label={l10n.getString("test-metodology", null, "test-metodology")}
                                        editor={metodologyEditor}
                                    />

                                </Box>
                                {/* AI button po prawej */}
                                <Tooltip title={l10n.getString("test-edit-ai-question", null, "_Edit AI question")}>
                                    <span>
                                        <IconButton
                                            color="primary"
                                            size="small"
                                            disabled={!editable || generatingMethodology}
                                            onClick={handleOpenMethodologyQuestion}
                                            sx={{ mt: "28px" }}
                                        >
                                            <AutoFixHighIcon
                                                sx={{
                                                    "@keyframes spin": {
                                                        "0%": { transform: "rotate(0deg)" },
                                                        "100%": { transform: "rotate(360deg)" },
                                                    },
                                                    animation: generatingMethodology ? "spin 0.8s linear infinite" : "none",
                                                }}
                                            />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <Tooltip title={l10n.getString("test-generate-system-prompt", null, "_Generate with system prompt")}>
                                    <span>
                                        <IconButton
                                            color="secondary"
                                            size="small"
                                            disabled={!editable || generatingMethodology}
                                            onClick={handleQuickMethodology}
                                            sx={{ mt: "28px" }}
                                        >
                                            <AutoFixNormalIcon />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Box>
                        </Paper>

                    </Box>
                )}
                {mainTab === 2 && (
                    <>
                        {hasAnyPhases ? (
                            <>
                                {hasPhaseTabs && (
                                    <Tabs value={phaseTab} onChange={handlePhaseTabChange} variant="scrollable" allowScrollButtonsMobile>
                                        {testPhases.map((phase) => (
                                            <Tab
                                                key={phase.id}
                                                label={
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                        <span>{phase.phaseType}</span>
                                                        {!phase.isActive && (<RemoveCircleOutlineIcon color="error" fontSize="small" />)}
                                                        {phase.isActive && (<CheckCircleOutlineIcon color="success" fontSize="small" />)}
                                                    </Box>
                                                }
                                            />
                                        ))}
                                    </Tabs>
                                )}

                                {hasPhaseTabs
                                    ? testPhases.map((phase, index) =>
                                        phaseTab === index ? (
                                            <DocumentTestPhaseTab
                                                key={phase.id}
                                                phase={phase}
                                                onUpdateSteps={(updatedSteps) => updatePhaseSteps(phase.id, updatedSteps)}
                                                onUpdatePhaseSubType={(subType) => updatePhaseSubType(phase.id, subType)}
                                                editable={editable}
                                                documentId={documentId}
                                                testName={testName}
                                                testDescription={testDescription}
                                                testMethodology={testMetodology}
                                            />
                                        ) : null
                                    )
                                    : (
                                        <DocumentTestPhaseTab
                                            key={testPhases[0].id}
                                            phase={testPhases[0]}
                                            onUpdateSteps={(updatedSteps) => updatePhaseSteps(testPhases[0].id, updatedSteps)}
                                            onUpdatePhaseSubType={(subType) => updatePhaseSubType(testPhases[0].id, subType)}
                                            editable={editable}
                                            documentId={documentId}
                                            testName={testName}
                                            testDescription={testDescription}
                                            testMethodology={testMetodology}
                                        />
                                    )}
                            </>
                        ) : (
                            <Typography variant="body2" color="text.secondary">Brak faz testu do edycji.</Typography>
                        )}
                    </>
                )}
            </Box>
        </Box>
    }

    let browser: ReactNode = <></>
    if (currentDoc) {
        switch (currentDoc.type) {
            case 'URS':
                browser = <UrsRequirementBrowser
                    ursId={currentDoc.id.toString()}
                    selectedItem={relatedObject && relatedObject.tag === 'URS' ? relatedObject.data : null}
                    setSelectedItem={setRelatedUrsRequirement}
                />
                break;
            case 'RA':
                browser = <RiskAnalysisFunctionBrowser
                    raId={currentDoc.id.toString()}
                    selectedItem={relatedObject && relatedObject.tag === 'RA' ? relatedObject.data : null}
                    setSelectedItem={setRelatedFunction}
                />
                break;
        }
    }

    return (<>
        <Dialog
            open
            fullWidth
            maxWidth={fullScreen ? undefined : 'lg'}
            fullScreen={fullScreen}
            PaperProps={{
                sx: (t) => ({
                    border: `1px solid ${t.palette.divider}`,
                    boxShadow: t.shadows[8],
                    overflow: 'hidden',
                    ...(fullScreen
                        ? { m: 2, height: 'calc(100% - 32px)', borderRadius: 3 }
                        : { borderRadius: 2 }),
                }),
            }}
        >
            <DialogTitle
                sx={(t) => ({
                    bgcolor: t.palette.grey[200],
                    borderBottom: `1px solid ${t.palette.divider}`,
                    py: 1.25,
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                })}
            >
                {title}
                <IconButton onClick={toggleFullScreen} style={{ float: 'right' }} color="primary">
                    {fullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                </IconButton>
            </DialogTitle>

            <DialogContent>
                {showComments
                    ? <SectionComments sectionId={sectionId} contextType='test' contextGuid={testSpecification?.guid ?? null} component={content} />
                    : content}
            </DialogContent>

            <DialogActions>
                <LoadingButton
                    loading={saving}
                    onClick={handleSave}
                    startIcon={<SaveIcon />}
                    size="small"
                    color="success"
                    variant="contained"
                    disabled={!editable}
                >
                    <Localized id="save"><span>save</span></Localized>
                </LoadingButton>
                <LoadingButton loading={saving} onClick={onCancel} size="small" variant="outlined">
                    <Localized id="cancel"><span>_cancel</span></Localized>
                </LoadingButton>
            </DialogActions>
        </Dialog>
        {generationQuestion && (
            <AiGenerationQuestionDialog
                titleId={generationQuestion.type === 'test-description' ? 'test-generate-description' : 'test-generate-methodology'}
                question={generationQuestion.question}
                generating={generatingDescription || generatingMethodology}
                error={generationError}
                onDismissError={() => setGenerationError(null)}
                onClose={() => setGenerationQuestion(null)}
                onGenerate={generationQuestion.type === 'test-description' ? handleGenerateDescription : handleGenerateMethodology}
            />
        )}
        <Modal open={assignModal}>
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                bgcolor: 'background.paper',
                p: 2,
            }}>
                <Box>
                    <Autocomplete
                        sx={{
                            marginTop: 1,
                        }}
                        options={projectDocs}
                        value={currentDoc}
                        onChange={handleStageChange}
                        getOptionLabel={({ title, type, version, no }) => `${type} Nr: ${no}, Tytuł: ${title}, v. ${version}`}
                        renderInput={params =>
                            <TextField {...params}
                                label={`Wybierz dokument`}
                            />
                        }
                    />
                </Box>
                <Box sx={{
                    flexGrow: 1,
                    overflowY: 'auto',
                }}>
                    {browser}
                </Box>
                <Box>
                    <Button variant='contained' onClick={handlecancelAssignClick}>OK</Button>
                </Box>
            </Box>
        </Modal>
    </>);
};

const RelatedObjectPanel = (props: { data: RelatedObject | null, actions: ReactNode }) => {
    const { data, actions } = props

    if (data === null) return null

    let content: ReactNode = <></>

    switch (data.tag) {
        case 'URS':
            if (data.loaded) {
                content = <>
                    <Box sx={{
                        display: 'flex',
                        gap: .5,
                        alignItems: 'center',
                    }}>
                        <Typography sx={{ ml: 2 }} variant='caption'>Nr dokumentu:</Typography>
                        <Chip label={data.docSymbol} />
                        <Typography sx={{ ml: 2 }} variant='caption'>Typ dokumentu:</Typography>
                        <Chip label='URS' />
                        {actions}
                    </Box>
                    <Divider />
                    <Box sx={{
                        display: 'flex',
                        gap: 2,
                    }}>
                        <Chip label={data.data.numeration} />
                        <div className='content' dangerouslySetInnerHTML={{ __html: data.data.name }}></div>
                    </Box>
                </>
            } else {
                content = <Box sx={{
                    display: 'flex',
                    gap: 2,
                }}>
                    <Typography color='text.secondary'>
                        {`Requirement id: ${data.data.id}: content not loaded`}
                    </Typography>
                    {actions}
                </Box>
            }
            break;
        case 'RA':
            if (data.loaded) {
                content = <>
                    <Box sx={{
                        display: 'flex',
                        gap: .5,
                        alignItems: 'center',
                    }}>
                        <Typography sx={{ ml: 2 }} variant='caption'>Nr dokumentu:</Typography>
                        <Chip label={data.docSymbol} />
                        <Typography sx={{ ml: 2 }} variant='caption'>Typ dokumentu:</Typography>
                        <Chip label='Analiza ryzyka' />
                        {actions}
                    </Box>
                    <Divider />
                    <Box sx={{
                        display: 'grid',
                        // Function on the left, its defects on the right; stacked on
                        // narrow screens, and a single column when there are none.
                        gridTemplateColumns: namedDefects(data.data.defects).length > 0
                            ? { xs: '1fr', md: '1fr 1fr' }
                            : '1fr',
                        gap: 2,
                    }}>
                        <Box sx={{
                            display: 'flex',
                            gap: 2,
                            alignItems: 'flex-start',
                        }}>
                            <Chip label={data.data.numeration} />
                            <div dangerouslySetInnerHTML={{ __html: data.data.name }}></div>
                        </Box>
                        <RelatedDefects defects={data.data.defects} />
                    </Box>
                </>
            } else {
                content = <Box sx={{
                    display: 'flex',
                    gap: 2,
                }}>
                    <Typography color='text.secondary'>
                        {`Function id: ${data.data.id}: content not loaded`}
                    </Typography>
                    {/* {actions} */}
                </Box>
            }
    }

    return <Paper sx={{
        p: 1,
        bgcolor: 'var(--gray-50)',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
    }}>
        {content}
    </Paper>
}

// Defect names are Tiptap HTML; an emptied editor leaves `<p></p>` behind, which
// would otherwise show up as a blank bullet.
const namedDefects = (defects: RiskAnalysisDefect[] | undefined) =>
    (defects ?? []).filter(d => d.name && d.name !== '<p></p>')

const RelatedDefects = ({ defects }: { defects: RiskAnalysisDefect[] | undefined }) => {
    const named = namedDefects(defects)
    if (named.length === 0) return null

    return <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: .5,
        borderLeft: { md: 1 },
        borderTop: { xs: 1, md: 0 },
        borderColor: 'divider',
        pl: { md: 2 },
        pt: { xs: 1, md: 0 },
    }}>
        <Typography variant='caption' color='text.secondary'>
            <Localized id='test-related-defects'>Wady</Localized>
        </Typography>
        <Box component='ul' sx={{ m: 0, pl: 3, typography: 'body2', '& p': { m: 0 } }}>
            {named.map(d => <li key={d.id} dangerouslySetInnerHTML={{ __html: d.name }} />)}
        </Box>
    </Box>
}
